import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const sessionId = params.get("session_id");
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[auth-callback] hash present:", !!hash, "session_id present:", !!sessionId);
      }
      if (!sessionId) {
        navigate("/login?error=no_session_id", { replace: true });
        return;
      }
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.log("[auth-callback] session exchange ok, user:", data?.email);
        }
        if (data?.session_token) {
          // session_token is set as httpOnly cookie by the backend; we
          // don't persist it anywhere on the client. Presence check only.
          void data.session_token;
        }
        const u = await refresh();
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.log("[auth-callback] refresh result:", u ? "user_loaded" : "null");
        }
        // Clear the hash
        window.history.replaceState(null, "", window.location.pathname);
        if (!u) {
          navigate("/login?error=session_verify_failed", { replace: true });
          return;
        }
        navigate("/dashboard", { replace: true, state: { user: u } });
      } catch (e) {
        const status = e?.response?.status;
        const rawDetail = e?.response?.data?.detail;
        // FastAPI validation errors return `detail` as an array of dicts —
        // JSON.stringify it so it doesn't render as `[object Object]` in
        // the URL bar / error banner.
        let detail;
        if (typeof rawDetail === "string") {
          detail = rawDetail;
        } else if (rawDetail) {
          try {
            detail = JSON.stringify(rawDetail).slice(0, 300);
          } catch {
            detail = String(rawDetail);
          }
        } else {
          detail = e?.message || "unknown";
        }
        // Keep console.error in prod — helpful for user-reported auth issues.
        // eslint-disable-next-line no-console
        console.error("[auth-callback] exchange failed", { status, detail });
        const reason = status === 401
          ? "session_expired_or_consumed"
          : status === 403
            ? "access_denied"
            : status === 502
              ? "upstream_unreachable"
              : status === 500
                ? "server_error"
                : status
                  ? `exchange_${status}`
                  : "network_error";
        navigate(
          `/login?error=exchange_failed&reason=${encodeURIComponent(reason)}&detail=${encodeURIComponent(detail)}`,
          { replace: true },
        );
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a] text-white">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-slate-700 border-t-blue-500 animate-spin" />
        <p className="text-sm text-slate-400 uppercase tracking-widest">Authenticating…</p>
      </div>
    </div>
  );
}
